import { Subject, debounceTime, takeUntil } from 'rxjs';

import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';

export interface ExtraActionConfig {
  icon: string;
  tooltip?: string;
  ariaLabel?: string;
  onClick?: () => void;
  show?: boolean;
}

@Component({
  selector: 'phoenix-expandable-panel',
  template: `
    <div class="relative">
      @if (showLoadingBar && isLoading) {
        <div class="absolute top-0 left-0 w-full z-10">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>
      }

      <mat-accordion
        (window:scroll)="scrollMainWindow()"
        [attr.aria-label]="ariaLabel"
      >
        <mat-expansion-panel #panel class="!mb-1">
          <mat-expansion-panel-header (click)="swapPanel()">
            <mat-panel-title
              [ngClass]="panelTitleClass"
              class="block text-left theme-text-2"
              [attr.aria-label]="'panel-title'"
            >
              {{ panelTitle }}
            </mat-panel-title>

            <mat-panel-description
              class="flex gap-x-2 justify-end items-center"
              [attr.aria-label]="'panel-description'"
            >
              <ng-content select="[extraOptions]"></ng-content>
              <!-- If a url is not sent as a parameter, the infohelper will not be displayed  -->
              @if (infoHelperURL !== '') {
                <phoenix-info-helper
                  [link]="infoHelperURL"
                  [position]="'below'"
                  (click)="$event.stopPropagation()"
                ></phoenix-info-helper>

                <!-- By default the component will be "collapseOnScroll" if not specified otherwise -->
                <!-- The pin button is shown only if the panel is "collapseOnScroll" -->
              }
              @if (extraActionConfig?.show && opened) {
                <button
                  class="material-icons"
                  mat-icon-button
                  [matTooltip]="extraActionConfig?.tooltip || ''"
                  [attr.aria-label]="
                    extraActionConfig?.ariaLabel || 'extra-action-button'
                  "
                  (click)="handleExtraAction($event)"
                >
                  {{ extraActionConfig?.icon }}
                </button>
              }

              @if (collapseOnScroll) {
                <button
                  class="material-icons"
                  [ngClass]="{ 'material-icons-outlined': !pinned }"
                  mat-icon-button
                  matTooltip="Keep this panel open"
                  aria-label="pin-button"
                  (click)="swapPin($event)"
                >
                  push_pin
                </button>
              }
            </mat-panel-description>
          </mat-expansion-panel-header>

          <ng-content> </ng-content>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  `,
  styles: [
    `
      * {
        border-color: none;
      }
    `,
  ],
  standalone: false,
})
export class ExpandablePanelComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input({ required: true }) ariaLabel = '';
  @Input() panelTitle = '';
  @Input() panelTitleClass = '';
  @Input() infoHelperURL = '';
  @Input() collapseOnScroll = true;
  @Input() showLoadingBar = false;
  @Input() isLoading = false;
  @Input() initOpened = false;
  @Input() extraActionConfig?: ExtraActionConfig;
  @Input() triggerCollapse = false;

  @Output() panelOpened = new EventEmitter<boolean>();
  @Output() extraActionClicked = new EventEmitter<void>();
  @Output() triggerCollapseChange = new EventEmitter<boolean>();

  scrollDispatcher = inject(ScrollDispatcher);
  destroy$ = new Subject<void>();

  opened = false;
  pinned = false;

  @ViewChild('panel') panelContent: { close: () => void } | undefined;

  ngOnInit(): void {
    if (this.collapseOnScroll) {
      this.scrollDispatcher
        .scrolled()
        .pipe(takeUntil(this.destroy$), debounceTime(30))
        .subscribe(scrollable => {
          if (scrollable && this.opened) {
            const element = scrollable.getElementRef().nativeElement;
            if (element.hasAttribute('data-scroll-target')) {
              this.scrollMainWindow();
            }
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['triggerCollapse'] &&
      this.opened &&
      !this.pinned &&
      this.triggerCollapse
    ) {
      this.panelContent?.close();
      this.opened = false;
    }

    setTimeout(() => {
      this.triggerCollapse = false;
      this.triggerCollapseChange.emit(this.triggerCollapse);
    });
  }

  ngAfterViewInit() {
    if (localStorage.getItem(`pinned_` + this.ariaLabel) === 'true') {
      this.opened = true;
      this.pinned = true;
      (this.panelContent as MatExpansionPanel)?.open();
    }

    if (this.initOpened) {
      setTimeout(() => {
        this.opened = true;
        (this.panelContent as MatExpansionPanel)?.open();
      });
    }
  }

  swapPanel() {
    if (this.opened) {
      this.panelContent?.close();
      this.opened = false;
    } else {
      (this.panelContent as MatExpansionPanel)?.open();
      this.opened = true;
    }
    this.panelOpened.emit(this.opened);
  }

  scrollMainWindow() {
    if (this.collapseOnScroll && !this.pinned && this.opened) {
      this.panelContent?.close();
      this.opened = false;
    }
  }

  swapPin(e?: Event) {
    e?.stopPropagation();
    this.pinned = !this.pinned;

    localStorage.setItem('pinned_' + this.ariaLabel, `${this.pinned}`);

    if (!this.opened && this.pinned) {
      this.opened = true;
      (this.panelContent as MatExpansionPanel)?.open();
    }
  }

  handleExtraAction(event: any) {
    this.extraActionClicked.emit();
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
